import { useState } from 'react'
import {
  Button,
  Input,
  Label,
  AuthLayout,
  Field,
  FieldGroup,
  Fieldset,
  Heading,
  NeutralPanel,
  StatusText,
  SurfacePanel,
  Text,
} from '../../components/ui'
import { ArrowRightIcon, UserPlusIcon } from '@heroicons/react/20/solid'
import { uiClasses } from '../shared/uiClasses'
import { authenticateUser, registerUser } from './authStorage'
import type { FormEvent } from 'react'
import type { MemberIdentity } from './types'

interface DemoAccount extends MemberIdentity {
  label: string
}

const DEMO_ACCOUNTS: DemoAccount[] = [
  { label: 'Continue as Member', name: 'Sarah Parker', email: 'sarah@albertohealth.demo' },
  { label: 'Continue as Clinician', name: 'Dr. Alex Kim', email: 'alex.kim@albertohealth.demo' },
]

type LoginMode = 'mock' | 'signin' | 'signup'

interface LoginPageProps {
  mode: LoginMode
  onModeChange: (mode: LoginMode) => void
  onMockLogin: (member: MemberIdentity) => void
  onRealLogin: (member: MemberIdentity) => void
}

function normalizeEmailInput(value: string): string {
  return value.trim().toLowerCase()
}

function isValidEmail(value: string): boolean {
  // Keep this intentionally permissive; authStorage still performs canonical normalization.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function formatAuthError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return 'Authentication failed. Please try again.'
}

export default function LoginPage({ mode, onModeChange, onMockLogin, onRealLogin }: LoginPageProps) {
  const [demoName, setDemoName] = useState('Sarah Parker')
  const [demoEmail, setDemoEmail] = useState('sarah@albertohealth.demo')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  function resetRealAuthFields() {
    setName('')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
  }

  function switchMode(nextMode: LoginMode) {
    const currentIsRealMode = mode === 'signin' || mode === 'signup'
    const nextIsRealMode = nextMode === 'signin' || nextMode === 'signup'

    onModeChange(nextMode)
    setError(null)
    setLoading(false)

    // Preserve real-auth draft values when toggling between sign-in and sign-up.
    if (currentIsRealMode && nextIsRealMode) {
      setPassword('')
      setConfirmPassword('')
      return
    }

    resetRealAuthFields()
  }

  function handleDemoSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const normalizedDemoName = demoName.trim()
    const normalizedDemoEmail = normalizeEmailInput(demoEmail)

    if (!normalizedDemoName) {
      setError('Name is required.')
      return
    }

    if (!isValidEmail(normalizedDemoEmail)) {
      setError('Enter a valid email address.')
      return
    }

    setDemoName(normalizedDemoName)
    setDemoEmail(normalizedDemoEmail)
    onMockLogin({ email: normalizedDemoEmail, name: normalizedDemoName })
  }

  async function handleSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    const normalizedEmail = normalizeEmailInput(email)

    if (!isValidEmail(normalizedEmail)) {
      setError('Enter a valid email address.')
      return
    }

    if (!password) {
      setError('Password is required.')
      return
    }

    setEmail(normalizedEmail)
    setLoading(true)

    try {
      const result = await authenticateUser(normalizedEmail, password)
      if (result.ok === false) {
        setError(result.error)
        return
      }

      onRealLogin({ email: result.user.email, name: result.user.name })
    } catch (authError) {
      setError(formatAuthError(authError))
    } finally {
      setLoading(false)
    }
  }

  async function handleSignUp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    const normalizedName = name.trim()
    const normalizedEmail = normalizeEmailInput(email)

    if (normalizedName.length < 2) {
      setError('Enter your full name.')
      return
    }

    if (!isValidEmail(normalizedEmail)) {
      setError('Enter a valid email address.')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setName(normalizedName)
    setEmail(normalizedEmail)
    setLoading(true)

    try {
      const result = await registerUser(normalizedEmail, normalizedName, password)
      if (result.ok === false) {
        setError(result.error)
        return
      }

      onRealLogin({ email: result.user.email, name: result.user.name })
    } catch (authError) {
      setError(formatAuthError(authError))
    } finally {
      setLoading(false)
    }
  }

  const isRealMode = mode === 'signin' || mode === 'signup'
  const isSignInMode = mode === 'signin'
  const isSignUpMode = mode === 'signup'
  const normalizedEmailDraft = normalizeEmailInput(email)
  const normalizedNameDraft = name.trim()
  const canSubmitSignIn = isValidEmail(normalizedEmailDraft) && password.length > 0 && !loading
  const canSubmitSignUp =
    normalizedNameDraft.length >= 2 &&
    isValidEmail(normalizedEmailDraft) &&
    password.length >= 6 &&
    confirmPassword.length > 0 &&
    password === confirmPassword &&
    !loading
  const pageTitle = isSignUpMode ? 'Create your Alberto Health account' : 'Welcome'
  const pageSubtitle = mode === 'mock'
    ? 'Try the demo workspace instantly, or switch to email for a persistent local account.'
    : 'Please sign in or sign up below.'

  return (
    <div className="unified-shell ui-shell auth-view landing-theme">
      <AuthLayout>
        <section className="auth-polish-shell w-full max-w-2xl">
          <div className="auth-polish-card rounded-xl p-4 sm:p-6">
            <Heading className={`auth-polish-title mt-5 text-2xl/8 font-semibold tracking-[-0.02em] sm:text-3xl/10 ${uiClasses.textPrimary}`}>
              {pageTitle}
            </Heading>
            <Text className={`auth-polish-subtitle ${uiClasses.sectionCopy} mt-3 max-w-2xl`}>
              {pageSubtitle}
            </Text>

            {/* Mode toggle */}
            <SurfacePanel className="auth-polish-mode-toggle mt-6 p-1">
              <div className="grid grid-cols-2 gap-1">
                <Button
                  variant={mode === 'mock' ? 'default' : 'outline'}
                  className={`auth-polish-mode-button ${mode === 'mock' ? 'auth-polish-mode-button-active' : ''} w-full justify-center`}
                  onClick={() => switchMode('mock')}
                >
                  Demo
                </Button>
                <Button
                  variant={isRealMode ? 'default' : 'outline'}
                  className={`auth-polish-mode-button ${isRealMode ? 'auth-polish-mode-button-active' : ''} w-full justify-center`}
                  onClick={() => switchMode('signin')}
                >
                  Email
                </Button>
              </div>
            </SurfacePanel>

            {isRealMode ? (
              <SurfacePanel className="auth-polish-auth-kind mt-3 p-1">
                <div className="grid grid-cols-2 gap-1">
                  <Button
                    variant={isSignInMode ? 'default' : 'outline'}
                    className={`auth-polish-kind-button ${isSignInMode ? 'auth-polish-kind-button-active' : ''} w-full justify-center`}
                    onClick={() => switchMode('signin')}
                  >
                    Sign in
                  </Button>
                  <Button
                    variant={isSignUpMode ? 'default' : 'outline'}
                    className={`auth-polish-kind-button ${isSignUpMode ? 'auth-polish-kind-button-active' : ''} w-full justify-center`}
                    onClick={() => switchMode('signup')}
                  >
                    Sign up
                  </Button>
                </div>
              </SurfacePanel>
            ) : null}

            {/* Error display */}
            {error && (
              <NeutralPanel tone="info" className="auth-polish-alert mt-4 rounded-lg px-4 py-3">
                <StatusText>{error}</StatusText>
              </NeutralPanel>
            )}

            {/* ─── Demo mode ─────────────────────────────── */}
            {mode === 'mock' && (
              <>
                <div className="auth-polish-demo-shortcuts auth-polish-provider-stack mt-6 grid gap-3 sm:grid-cols-2">
                  {DEMO_ACCOUNTS.map((account) => (
                    <Button
                      key={account.label}
                      variant="outline"
                      className="auth-polish-demo-shortcut justify-center"
                      onClick={() => onMockLogin({ name: account.name, email: account.email })}
                    >
                      {account.label}
                    </Button>
                  ))}
                </div>

                <form onSubmit={handleDemoSubmit} className="auth-polish-form mt-8">
                  <Fieldset>
                    <FieldGroup>
                      <Field>
                        <Label htmlFor="demo-name">Full name</Label>
                        <Input
                          id="demo-name"
                          name="name"
                          autoComplete="name"
                          value={demoName}
                          onChange={(event) => {
                            setDemoName(event.target.value)
                            setError(null)
                          }}
                          required
                        />
                      </Field>
                      <Field>
                        <Label htmlFor="demo-email">Email</Label>
                        <Input
                          id="demo-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          inputMode="email"
                          spellCheck={false}
                          value={demoEmail}
                          onChange={(event) => {
                            setDemoEmail(event.target.value)
                            setError(null)
                          }}
                          required
                        />
                      </Field>
                      <Button type="submit" className="w-full">
                        Enter demo dashboard
                        <ArrowRightIcon data-slot="icon" />
                      </Button>
                      <p className={`auth-polish-secondary-label ${uiClasses.textSubtle}`}>Need a persistent account?</p>
                      <Button
                        variant="ghost"
                        type="button"
                        className="w-full justify-center"
                        onClick={() => switchMode('signin')}
                      >
                        Use email sign in
                      </Button>
                    </FieldGroup>
                  </Fieldset>
                </form>
              </>
            )}

            {/* ─── Sign In mode ──────────────────────────── */}
            {mode === 'signin' && (
              <form onSubmit={handleSignIn} className="auth-polish-form mt-8">
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        inputMode="email"
                        spellCheck={false}
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value)
                          setError(null)
                        }}
                        required
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="signin-password">Password</Label>
                      <Input
                        id="signin-password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value)
                          setError(null)
                        }}
                        required
                      />
                    </Field>
                    <div className="auth-polish-action-row mt-2 flex flex-col items-stretch gap-3">
                      <Button type="submit" className="w-full" disabled={!canSubmitSignIn}>
                        {loading ? 'Signing in...' : 'Sign in'}
                        <ArrowRightIcon data-slot="icon" />
                      </Button>
                    </div>
                    <div className="auth-polish-divider" aria-hidden="true" />
                    <div className="auth-polish-provider-stack mt-3 grid gap-3">
                      <Button
                        variant="ghost"
                        className="w-full justify-center"
                        onClick={() => switchMode('signup')}
                        type="button"
                      >
                        <UserPlusIcon data-slot="icon" />
                        Create an account
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-center"
                        onClick={() => switchMode('mock')}
                        type="button"
                      >
                        Use demo workspace
                      </Button>
                    </div>
                  </FieldGroup>
                </Fieldset>
              </form>
            )}

            {/* ─── Sign Up mode ──────────────────────────── */}
            {mode === 'signup' && (
              <form onSubmit={handleSignUp} className="auth-polish-form mt-8">
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label htmlFor="signup-name">Full name</Label>
                      <Input
                        id="signup-name"
                        name="name"
                        autoComplete="name"
                        value={name}
                        onChange={(event) => {
                          setName(event.target.value)
                          setError(null)
                        }}
                        required
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        inputMode="email"
                        spellCheck={false}
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value)
                          setError(null)
                        }}
                        required
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="signup-password">Password</Label>
                      <Input
                        id="signup-password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        minLength={6}
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value)
                          setError(null)
                        }}
                        placeholder="At least 6 characters"
                        required
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="signup-confirm">Confirm password</Label>
                      <Input
                        id="signup-confirm"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        minLength={6}
                        value={confirmPassword}
                        onChange={(event) => {
                          setConfirmPassword(event.target.value)
                          setError(null)
                        }}
                        required
                      />
                    </Field>
                    <div className="auth-polish-action-row mt-2 flex flex-col items-stretch gap-3">
                      <Button type="submit" className="w-full" disabled={!canSubmitSignUp}>
                        {loading ? 'Creating account...' : 'Create account & enter'}
                        <ArrowRightIcon data-slot="icon" />
                      </Button>
                    </div>
                    <div className="auth-polish-provider-stack mt-3 grid gap-3">
                      <Button
                        variant="ghost"
                        className="w-full justify-center"
                        onClick={() => switchMode('signin')}
                        type="button"
                      >
                        Already have an account? Sign in
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-center"
                        onClick={() => switchMode('mock')}
                        type="button"
                      >
                        Use demo workspace
                      </Button>
                    </div>
                  </FieldGroup>
                </Fieldset>
              </form>
            )}
          </div>
        </section>
      </AuthLayout>
    </div>
  )
}
